// sticky-card
import { onDestroy, onMount } from "@/modules/_";
import gsap, { ScrollTrigger, reduced } from "@lib/gsap";

const SCROLL_VH_PER_CARD = 100;
const CIRCLE_SIZE_VW = 250;
const CARD_REST_OFFSET_VH = 55;
const FAN_ANGLE_DEG = 4.5;
const CIRCLES_DRIFT_PCT = 5;
const SCRUB_EASE = "power1.out";
const FIRST_CARD_EARLY_VH = 60;
const CARD_LANDED_OFFSET_VH = 8;

// Inertia targets inner wrappers so hover tweens don't fight scrub transforms on outer cards
const INERTIA_XY_MULT = 25;
const INERTIA_ROT_MULT = 15;
const INERTIA_XY_CLAMP = 600;
const INERTIA_ROT_CLAMP = 45;
const INERTIA_RESISTANCE = 220;

export default function (element: HTMLElement) {
  const cards = Array.from(
    element.querySelectorAll<HTMLElement>('[sticky-card="card"]'),
  );
  if (cards.length === 0) return;

  const circles: HTMLElement[] = [];
  const inertiaTargets: HTMLElement[] = [];
  const triggers: ScrollTrigger[] = [];
  let pinHeight: HTMLElement | null = null;
  let container: HTMLElement | null = null;
  let circlesWrap: HTMLElement | null = null;
  let detachInertia: (() => void) | null = null;

  onMount(() => {
    gsap.set(element, {
      position: "relative",
      height: "auto",
      display: "block",
    });

    pinHeight = document.createElement("div");
    pinHeight.setAttribute("sticky-card-pin-height", "");

    container = document.createElement("div");
    container.setAttribute("sticky-card-container", "");
    pinHeight.appendChild(container);

    circlesWrap = document.createElement("div");
    circlesWrap.setAttribute("sticky-card-circles", "");
    container.appendChild(circlesWrap);

    cards.forEach((card) => {
      const circle = document.createElement("div");
      circle.setAttribute("sticky-card-circle", "");
      circle.appendChild(card);
      circlesWrap!.appendChild(circle);
      circles.push(circle);

      // Inner wrapper so inertia x/y/rotation composes with scrub transforms rather than overwriting them
      const inner = document.createElement("div");
      inner.setAttribute("sticky-card-inertia", "");
      while (card.firstChild) inner.appendChild(card.firstChild);
      card.appendChild(inner);
      inertiaTargets.push(inner);
    });

    element.appendChild(pinHeight);

    gsap.set(pinHeight, {
      height: `${cards.length * SCROLL_VH_PER_CARD}vh`,
    });
    gsap.set(container, { position: "relative", height: "100vh" });
    gsap.set(circlesWrap, { height: "100%" });

    gsap.set(circles, {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: `${CIRCLE_SIZE_VW}vw`,
      height: `${CIRCLE_SIZE_VW}vw`,
      borderRadius: "100%",
      xPercent: -50,
      yPercent: 0,
      pointerEvents: "none",
      willChange: "transform",
    });

    // Padding migrates to inner so bg fills card edge-to-edge while content stays inside original gutters
    const cardPadding = cards[0]
      ? window.getComputedStyle(cards[0]).padding || ""
      : "";
    gsap.set(cards, {
      position: "absolute",
      top: 0,
      left: "50%",
      xPercent: -50,
      yPercent: 0,
      y: `${CARD_REST_OFFSET_VH}vh`,
      padding: 0,
      pointerEvents: "auto",
      willChange: "transform",
      transformOrigin: "center center",
    });

    gsap.set(inertiaTargets, {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      padding: cardPadding,
      boxSizing: "border-box",
      willChange: "transform",
      transformOrigin: "center center",
    });

    if (reduced) {
      let rot = -((cards.length - 1) * FAN_ANGLE_DEG) / 2;
      circles.forEach((circle, i) => {
        gsap.set(circle, { rotation: rot });
        gsap.set(cards[i], {
          rotation: rot,
          y: `${CARD_LANDED_OFFSET_VH}vh`,
          yPercent: -50,
        });
        rot += FAN_ANGLE_DEG;
      });
      return;
    }

    const driftST = ScrollTrigger.create({
      trigger: pinHeight,
      start: "top top",
      end: "bottom bottom",
      pin: container,
      scrub: true,
      animation: gsap.fromTo(
        circlesWrap,
        { y: `${CIRCLES_DRIFT_PCT}%` },
        { y: `-${CIRCLES_DRIFT_PCT}%`, ease: "none" },
      ),
    });
    triggers.push(driftST);

    const distPerCard =
      (pinHeight.clientHeight - window.innerHeight) / cards.length;
    let rot = -((cards.length - 1) * FAN_ANGLE_DEG) / 2;

    circles.forEach((circle, index) => {
      const card = cards[index];

      // First card scrub starts before the pin engages so it's already arcing as the section approaches
      let startStr: string;
      let endStr: string;
      if (index === 0 && FIRST_CARD_EARLY_VH > 0) {
        const headStartPx = (FIRST_CARD_EARLY_VH * window.innerHeight) / 100;
        startStr = `top top+=${headStartPx}`;
        endStr = `+=${distPerCard + headStartPx}`;
      } else {
        const startPx = distPerCard * index;
        startStr = `top top-=${startPx}`;
        endStr = `+=${distPerCard}`;
      }

      const circleST = ScrollTrigger.create({
        trigger: pinHeight,
        start: startStr,
        end: endStr,
        scrub: true,
        animation: gsap.to(circle, { rotation: rot, ease: SCRUB_EASE }),
      });
      triggers.push(circleST);

      const cardST = ScrollTrigger.create({
        trigger: pinHeight,
        start: startStr,
        end: endStr,
        scrub: true,
        animation: gsap.to(card, {
          rotation: rot,
          y: `${CARD_LANDED_OFFSET_VH}vh`,
          yPercent: -50,
          ease: SCRUB_EASE,
        }),
      });
      triggers.push(cardST);

      rot += FAN_ANGLE_DEG;
    });

    const canHover = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;

    if (canHover) {
      let prevX = 0;
      let prevY = 0;
      let velX = 0;
      let velY = 0;
      let rafId: number | null = null;

      const onMove = (e: MouseEvent) => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          velX = e.clientX - prevX;
          velY = e.clientY - prevY;
          prevX = e.clientX;
          prevY = e.clientY;
          rafId = null;
        });
      };
      element.addEventListener("mousemove", onMove);

      const clampXY = gsap.utils.clamp(-INERTIA_XY_CLAMP, INERTIA_XY_CLAMP);
      const clampRot = gsap.utils.clamp(
        -INERTIA_ROT_CLAMP,
        INERTIA_ROT_CLAMP,
      );

      const cleanups: Array<() => void> = [];

      cards.forEach((card, i) => {
        const target = inertiaTargets[i];
        const onEnter = (e: MouseEvent) => {
          // Signed cross product of cursor-offset × velocity gives angular force; divide by lever distance to decouple from pointer distance
          const rect = target.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const ox = e.clientX - cx;
          const oy = e.clientY - cy;
          const rawTorque = ox * velY - oy * velX;
          const leverDist = Math.hypot(ox, oy) || 1;
          const angularForce = rawTorque / leverDist;

          gsap.to(target, {
            inertia: {
              x: { velocity: clampXY(velX * INERTIA_XY_MULT), end: 0 },
              y: { velocity: clampXY(velY * INERTIA_XY_MULT), end: 0 },
              rotation: {
                velocity: clampRot(angularForce * INERTIA_ROT_MULT),
                end: 0,
              },
              resistance: INERTIA_RESISTANCE,
            },
            overwrite: "auto",
          });
        };
        card.addEventListener("mouseenter", onEnter);
        cleanups.push(() => card.removeEventListener("mouseenter", onEnter));
      });

      detachInertia = () => {
        element.removeEventListener("mousemove", onMove);
        if (rafId !== null) cancelAnimationFrame(rafId);
        cleanups.forEach((fn) => fn());
      };
    }
  });

  onDestroy(() => {
    triggers.forEach((t) => t.kill());
    detachInertia?.();
    detachInertia = null;

    inertiaTargets.forEach((inner, i) => {
      const card = cards[i];
      gsap.killTweensOf(inner);
      while (inner.firstChild) card.appendChild(inner.firstChild);
      inner.remove();
    });
    inertiaTargets.length = 0;

    circles.forEach((circle, i) => {
      const card = cards[i];
      gsap.killTweensOf(card);
      gsap.killTweensOf(circle);
      element.appendChild(card);
      gsap.set(card, {
        clearProps:
          "transform,position,top,left,xPercent,yPercent,x,y,rotation,padding,willChange,transformOrigin,pointerEvents",
      });
    });
    circles.length = 0;

    pinHeight?.remove();
    pinHeight = null;
    container = null;
    circlesWrap = null;

    gsap.set(element, {
      clearProps: "position,height,display",
    });
  });
}
